import api from './api';
import { Filtros, buildQueryParams } from './filterService';

// --- Interfaces existentes ---
export interface ImovelMapa {
  id: number;
  nome: string;
  construtora: string;
  bairro: string;
  tipo: string[];
  preco: number | null;
  status: string;
  unidades_totais: number;
  perc_vendido: number;
  latitude: number;
  longitude: number;
  imagens: string[];
}

// --- Nova interface para resumo dos empreendimentos ---
export interface ResumoMapa {
  preco_m2_medio: number;
  preco_medio_produto: number;
  valor_minimo: number;
  valor_maximo: number;
  valor_medio: number;
  area_media: number;
  unidades_lancadas: number;
  unidades_vendidas: number;
  unidades_distrato: number;
  total_unidades: number;
  vso: number;
  ofertas_mes_anterior: number;
  vendas_mes_atual: number;
}

// --- Busca de comércios --- //
export interface ComercioMapa {
  id: number;
  cnaes_secundarios: {
    codigo: string;
    descricao: string;
  }[];
  cnae_principal: {
    codigo: string;
    descricao: string;
  };
  cnae_codigo: string;
  cnpj: string;
  nome_fantasia: string;
  situacao_cadastral: number;
  data_situacao_cadastral: string;
  motivo_situacao_cadastral: string;
  data_inicio_atividade: string;
  telefone_principal: string;
  telefone_secundario: string;
  email: string;
  latitude: number;
  longitude: number;
  descricao_endereco: string;
  municipio: string;
  bairro: string;
  location: string;

  distancia?: number;
  categoria?: string;
  endereco?: string;
}

export interface MapaComercioResponse {
  comercios: ComercioMapa[];
}

// --- Novo formato da resposta da API ---
export interface MapaResponse {
  resumo: ResumoMapa;
  empreendimentos: ImovelMapa[];
}

export interface MunicipioFeature {
  type: "Feature";
  geometry: any;
  properties: {
    id: string;
    nome: string;
    uf: string;
    densidade_demografica: number;
  };
}

export interface MunicipioMapaResponse {
  type: "FeatureCollection";
  features: MunicipioFeature[];
  centroGeral?: { latitude: number; longitude: number };
}

// --- Serviço unificado para obter empreendimentos ---
export const fetchEmpreendimentosMapa = async (filtros: Filtros, geojson?: any): Promise<MapaResponse> => {
  try {
    let data: MapaResponse;

    if (geojson) {
      const response = await api.post<MapaResponse>(
        "/poligono-empreendimento/filtrar/",
        geojson,
        { headers: { "Content-Type": "application/json" } }
      );
      data = response.data;
    } else {
      const query = buildQueryParams(filtros);
      const response = await api.get<ImovelMapa[]>(`/empreendimento-mapa/?${query}`);
      const empreendimentos = response.data ?? [];
      data = {
        resumo: gerarResumo(empreendimentos),
        empreendimentos,
      };
    }

    // Se o resumo não vier, calculamos dinamicamente
    const resumo = data.resumo ?? gerarResumo(data.empreendimentos);

    return { resumo, empreendimentos: data.empreendimentos ?? [] };
  } catch (error) {
    console.error("Erro ao buscar empreendimentos no mapa:", error);
    return { resumo: gerarResumo([]), empreendimentos: [] };
  }
};

export const fetchComerciosMapa = async (
  filtros: Filtros,
  geojson?: any
): Promise<MapaComercioResponse> => {
  try {
    let data: ComercioMapa[];

    if (geojson) {
      // Extrai o CNAE do objeto enviado pelo mapa
      const cnae = geojson.cnae ? String(geojson.cnae) : "";
      // Monta a URL com o query param
      const url = `/EstabelecimentoViewSet/por_poligono/?cnae=${encodeURIComponent(cnae)}`;
      const response = await api.post<ComercioMapa[]>(
        url,
        geojson, // O corpo continua contendo o GeoJSON
        { headers: { "Content-Type": "application/json" } }
      );

      data = response.data ?? [];
    } else {
      const query = buildQueryParams(filtros);
      const response = await api.get<ComercioMapa[]>(`/comercio-mapa/?${query}`);
      data = response.data ?? [];
    }

    return { comercios: data };
  } catch (error) {
    console.error("Erro ao buscar comércios no mapa:", error);
    return { comercios: [] };
  }
};



// --- Função para gerar resumo "dummy" caso a API não envie ---
const gerarResumo = (empreendimentos: ImovelMapa[]): ResumoMapa => {
  const totalUnidades = empreendimentos.reduce((acc, e) => acc + (e.unidades_totais ?? 0), 0);
  const unidadesVendidas = empreendimentos.reduce((acc, e) => acc + Math.round((e.unidades_totais ?? 0) * ((e.perc_vendido ?? 0)/100)), 0);
  const precoMedioProduto = empreendimentos.reduce((acc, e) => acc + (e.preco ?? 0), 0) / (empreendimentos.length || 1);
  const precoM2Medio = 0; // se não tiver info de área, deixa 0
  const valorMinimo = Math.min(...empreendimentos.map(e => e.preco ?? Infinity)) || 0;
  const valorMaximo = Math.max(...empreendimentos.map(e => e.preco ?? 0));
  const valorMedio = precoMedioProduto;
  const areaMedia = 0;
  const unidadesLancadas = totalUnidades;
  const unidadesDistrato = 0;
  const vso = totalUnidades ? Math.round((unidadesVendidas / totalUnidades) * 100) : 0;
  const ofertasMesAnterior = 0;
  const vendasMesAtual = unidadesVendidas;

  return {
    preco_m2_medio: precoM2Medio,
    preco_medio_produto: precoMedioProduto,
    valor_minimo: valorMinimo,
    valor_maximo: valorMaximo,
    valor_medio: valorMedio,
    area_media: areaMedia,
    unidades_lancadas: unidadesLancadas,
    unidades_vendidas: unidadesVendidas,
    unidades_distrato: unidadesDistrato,
    total_unidades: totalUnidades,
    vso,
    ofertas_mes_anterior: ofertasMesAnterior,
    vendas_mes_atual: vendasMesAtual
  };
};


// --- Busca de municípios ---
export const getMunicipiosMapa = async (municipios?: string[]): Promise<MunicipioMapaResponse> => {
  if (!municipios || municipios.length === 0) {
    return { type: "FeatureCollection", features: [], centroGeral: undefined };
  }

  try {
    const allFeatures: MunicipioFeature[] = [];
    let centroGeral: { latitude: number; longitude: number } | undefined;

    for (const municipioRaw of municipios) {
      const municipioNome = municipioRaw
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .toLowerCase()
        .trim();

      const municipioEncoded = encodeURIComponent(municipioNome);
      const response = await api.get(`/bairros?municipio=${municipioEncoded}`);

      const features: MunicipioFeature[] = (response.data["bairros"] ?? []).map((bairro: any) => {
        let geometry = null;
        try {
          geometry = JSON.parse(bairro.geom);
        } catch (e) {
          console.error("Erro ao parsear geom:", e);
        }
        return {
          type: "Feature",
          geometry,
          properties: {
            id: bairro.id,
            nome: bairro.nome,
            uf: bairro.uf,
            densidade_demografica: bairro.densidade_demografica,
          },
        };
      });

      allFeatures.push(...features);
      if (!centroGeral && response.data["centro_geral"]) {
        centroGeral = response.data["centro_geral"];
      }
    }

    return { type: "FeatureCollection", features: allFeatures, centroGeral };
  } catch (error) {
    console.error("Erro ao buscar municípios para o mapa:", error);
    return { type: "FeatureCollection", features: [], centroGeral: undefined };
  }
};

